import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { guestApi } from '@/api/guest';
import { formatCurrency, formatDate, formatPeriod } from '@/utils/format';

export function GuestPayPage() {
  const { t } = useTranslation();
  const { paymentId } = useParams<{ paymentId: string }>();
  const id = paymentId ? Number(paymentId) : null;
  const fileInput = useRef<HTMLInputElement>(null);
  const [kaspi, setKaspi] = useState<{ deep_link: string } | null>(null);

  const payment = useQuery({
    queryKey: ['guest-payment', id],
    queryFn: () => guestApi.payment(id as number),
    enabled: id !== null,
    retry: false,
  });

  const upload = useMutation({
    mutationFn: (form: FormData) => guestApi.uploadProof(id as number, form),
  });

  const loadKaspi = async () => {
    if (id === null) return;
    const res = await guestApi.kaspi(id);
    // res.qr_content — сырой payload; для сканируемого QR на десктопе нужна
    // QR-либа (например react-qr-code). Пока ведём по deep_link.
    setKaspi({ deep_link: res.deep_link });
  };

  if (payment.isLoading) return <Spinner />;
  if (payment.error) {
    return (
      <Card title={t('errors.link_expired')}>
        <p className="text-sm text-slate-600">{t('errors.link_expired_body')}</p>
      </Card>
    );
  }
  if (!payment.data) return null;

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    // Бэкенд ждёт поле `proof` (НЕ `file`).
    form.append('proof', file);
    await upload.mutateAsync(form);
    e.target.value = '';
  };

  return (
    <div className="mx-auto max-w-xl space-y-4 p-4">
      <Card title={t('payments.pay_title')}>
        <p className="text-sm text-slate-600">{payment.data.property.address}</p>
        <p className="mt-2 text-2xl font-semibold">
          {formatCurrency(payment.data.payment.amount_expected)}
        </p>
        <p className="text-xs text-slate-500">
          {formatPeriod(t, payment.data.payment.period_month, payment.data.payment.period_year)} —{' '}
          {t('payments.due_by')} {formatDate(payment.data.payment.due_date)}
        </p>
      </Card>

      <Card title={t('payments.pay_kaspi')}>
        {kaspi ? (
          <a
            href={kaspi.deep_link}
            className="block rounded-lg bg-brand px-4 py-2 text-center text-sm font-medium text-white"
          >
            {t('payments.open_kaspi')}
          </a>
        ) : (
          <Button onClick={() => void loadKaspi()}>{t('payments.pay_kaspi')}</Button>
        )}
      </Card>

      <Card title={t('payments.upload_proof')}>
        <input
          ref={fileInput}
          type="file"
          accept="image/*,application/pdf"
          onChange={onFile}
          className="hidden"
        />
        <Button
          variant="secondary"
          onClick={() => fileInput.current?.click()}
          loading={upload.isPending}
        >
          {t('payments.upload_proof')}
        </Button>
        {upload.isSuccess ? (
          <p className="mt-2 text-sm text-green-700">{t('payments.proof_uploaded')}</p>
        ) : null}
      </Card>
    </div>
  );
}
